import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import communityService, { type MyCommunityItem } from '@/services/communityService';
import { mabarService, type PublicMabarEvent } from '@/services/mabarService';
import { wilayahService, KotaOption } from '@/services/wilayahService';
import { CommunityListSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useGuestAuthAction } from '@/hooks/useGuestAuthAction';
import { getPaginationItems } from '@/utils/pagination';
import { formatLocalEventTime } from '@/utils/dateUtils';

// Helper for Initials
const getInitials = (name: string): string => {
  if (!name) return 'KM';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// Interface and Helper for Community Event Info (Future vs Past)
interface EventInfoResult {
  label: string;
  text: string;
  isFuture: boolean;
}

const formatEventInfo = (eventDateStr?: string, startTimeStr?: string): EventInfoResult | null => {
  if (!eventDateStr) return null;
  try {
    const d = new Date(eventDateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const eventCheck = new Date(d);
    eventCheck.setHours(23, 59, 59, 999);

    const isFuture = eventCheck.getTime() >= now.getTime();

    const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

    const dateNum = d.getDate();
    const monthName = months[d.getMonth()];
    const yearNum = d.getFullYear();

    if (isFuture) {
      const dayName = days[d.getDay()];
      const timeStr = formatLocalEventTime(startTimeStr);
      return {
        label: 'Event terdekat',
        text: `${dayName}, ${dateNum} ${monthName}${timeStr ? ` · ${timeStr}` : ''}`,
        isFuture: true,
      };
    } else {
      return {
        label: 'Event terakhir',
        text: `${dateNum} ${monthName} ${yearNum}`,
        isFuture: false,
      };
    }
  } catch {
    return null;
  }
};

// Helper for Calculating Community Activity Score & Status Label
const calculateActivityStatus = (eventDateStr?: string): { pct: number; label: string } => {
  if (!eventDateStr) {
    return { pct: 40, label: 'Tidak aktif' };
  }
  try {
    const d = new Date(eventDateStr);
    const now = new Date();
    // Difference in days (positive = past days, negative = future days)
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      // Event is today or in the future
      return { pct: 90, label: 'Sangat aktif' };
    } else if (diffDays <= 7) {
      // Event happened within the last 7 days
      return { pct: 80, label: 'Sangat aktif' };
    } else if (diffDays <= 30) {
      // Event happened within 30 days
      return { pct: 60, label: 'Aktif' };
    } else {
      // Event happened more than 30 days ago
      return { pct: 30, label: 'Tidak aktif' };
    }
  } catch {
    return { pct: 40, label: 'Tidak aktif' };
  }
};

// Location Dropdown Component
const LocationSearchDropdown: React.FC<{
  value: string;
  onChange: (cityName: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [locationOptions, setLocationOptions] = useState<KotaOption[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!query || query.trim().length < 3) {
      setLocationOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingLocation(true);
      const res = await wilayahService.searchKota(query);
      setLocationOptions(res);
      setIsSearchingLocation(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full h-11 pl-8 pr-6 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-semibold text-[#0E1726] focus:outline-none focus:border-[#FC480D] shadow-2xs flex items-center justify-between cursor-pointer truncate transition-colors"
      >
        <Icon
          icon="ph:map-pin-bold"
          className="w-4 h-4 text-gray-400 absolute left-2.5 pointer-events-none"
        />
        <span className="truncate">{value || 'Lokasi'}</span>
        <Icon
          icon="ph:caret-down-bold"
          className="w-3.5 h-3.5 text-gray-400 absolute right-2 pointer-events-none"
        />
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-64 md:w-72 bg-white rounded-2xl border border-[#E0E6ED] shadow-xl z-30 p-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="relative flex items-center">
            <Icon
              icon="ph:magnifying-glass-bold"
              className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none"
            />
            <input
              type="text"
              autoFocus
              placeholder="Cari Kota/Kabupaten (min. 3 huruf)..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-8 rounded-xl bg-gray-50 border border-gray-200 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D]"
            />
            <div className="absolute right-2.5 flex items-center">
              {isSearchingLocation ? (
                <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-[#FC480D]" />
              ) : query ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="text-gray-400 hover:text-gray-600 text-xs font-bold"
                >
                  ✕
                </button>
              ) : null}
            </div>
          </div>

          <div className="max-h-52 overflow-y-auto space-y-0.5 scrollbar-thin divide-y divide-gray-50">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition-colors flex items-center justify-between ${
                !value
                  ? 'bg-orange-50 text-[#FC480D] font-bold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Semua Lokasi</span>
              {!value && <Icon icon="ph:check-bold" className="w-3.5 h-3.5" />}
            </button>

            {query.trim().length > 0 && query.trim().length < 3 ? (
              <div className="py-3 px-3 text-center text-[11px] text-gray-400 font-medium">
                Ketik minimal 3 huruf untuk mencari kota...
              </div>
            ) : isSearchingLocation ? (
              <div className="py-4 text-center text-xs text-gray-400 font-medium flex items-center justify-center gap-2">
                <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-[#FC480D]" />
                <span>Mencari lokasi...</span>
              </div>
            ) : locationOptions.length === 0 && query.trim().length >= 3 ? (
              <div className="py-3 px-3 text-center text-[11px] text-gray-400 font-medium">
                Kota tidak ditemukan
              </div>
            ) : (
              locationOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.nama);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-xl text-xs transition-colors flex items-center justify-between ${
                    value === opt.nama
                      ? 'bg-orange-50 text-[#FC480D] font-bold'
                      : 'hover:bg-orange-50/60 text-[#0E1726]'
                  }`}
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="font-bold text-xs text-[#0E1726] truncate">{opt.nama}</div>
                    <div className="text-gray-400 text-[10px] truncate">{opt.namaLengkap}</div>
                  </div>
                  {value === opt.nama && (
                    <Icon icon="ph:check-bold" className="w-3.5 h-3.5 text-[#FC480D] shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CATEGORY_PILLS = [
  { id: 'Semua', label: 'Semua' },
  { id: 'Futsal', label: 'Futsal' },
  { id: 'Mini Soccer', label: 'Mini Soccer' },
  { id: 'Badminton', label: 'Badminton' },
  { id: 'Padel', label: 'Padel' },
  { id: 'Basket', label: 'Basket' },
];

const ClientDiscoveryCommunityPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const runGuestAware = useGuestAuthAction();

  const [loading, setLoading] = useState(true);
  const [discoverCommunities, setDiscoverCommunities] = useState<MyCommunityItem[]>([]);
  const [joinedSlugs, setJoinedSlugs] = useState<Set<string>>(new Set());
  const [upcomingEventsMap, setUpcomingEventsMap] = useState<Record<string, PublicMabarEvent>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Semua');
  const [selectedLocation, setSelectedLocation] = useState('');
  const ITEMS_PER_PAGE = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMetaItems, setTotalMetaItems] = useState(0);
  const [actionLoadingSlug, setActionLoadingSlug] = useState<string | null>(null);

  // Bottom Popup Confirmation State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    isClosing: boolean;
    type: 'join' | 'leave';
    slug: string;
    name: string;
  }>({
    isOpen: false,
    isClosing: false,
    type: 'join',
    slug: '',
    name: '',
  });

  // Change Mode Bottom Sheet State
  const [isModeModalOpen, setIsModeModalOpen] = useState(false);
  const [isModeModalClosing, setIsModeModalClosing] = useState(false);

  const fetchCommunities = useCallback(async () => {
    try {
      setLoading(true);
      setDiscoverCommunities([]);

      const discoverPromise = communityService.getDiscoverCommunities({
        search: searchQuery || undefined,
        category: selectedCategory !== 'Semua' ? selectedCategory : undefined,
        location: selectedLocation || undefined,
        page: currentPage,
        limit: 5,
        sort_by: 'members',
        sort: 'desc',
        sort_order: 'desc',
        order_by: 'total_members',
      });

      const [myRes, discRes, publicEventsRes] = await Promise.allSettled([
        isAuthenticated
          ? communityService.getMyCommunities()
          : Promise.resolve({ success: true, data: [] as MyCommunityItem[] }),
        discoverPromise,
        mabarService.getPublicEvents({ limit: 50 }),
      ]);

      let joined = new Set<string>();
      let myData: MyCommunityItem[] = [];
      if (myRes.status === 'fulfilled' && myRes.value.success) {
        myData = myRes.value.data || [];
        myData.forEach((item) => joined.add(item.slug));
      }
      setJoinedSlugs(joined);

      let eventsMap: Record<string, PublicMabarEvent> = {};
      if (publicEventsRes.status === 'fulfilled' && publicEventsRes.value.data) {
        publicEventsRes.value.data.forEach((evt) => {
          if (evt.host?.slug && !eventsMap[evt.host.slug]) {
            eventsMap[evt.host.slug] = evt;
          }
          if (evt.host?.name && !eventsMap[evt.host.name.toLowerCase()]) {
            eventsMap[evt.host.name.toLowerCase()] = evt;
          }
        });
      }
      setUpcomingEventsMap(eventsMap);

      let discData: MyCommunityItem[] = [];
      if (discRes.status === 'fulfilled' && discRes.value.success) {
        discData = discRes.value.data || [];
        // Urutkan komunitas berdasarkan jumlah anggota terbanyak (most members first)
        discData.sort((a, b) => (b.totalMembers || 0) - (a.totalMembers || 0));
        if (discRes.value.meta?.total !== undefined) {
          setTotalMetaItems(discRes.value.meta.total);
        } else {
          setTotalMetaItems(discData.length);
        }
      }

      setDiscoverCommunities(discData);
    } catch (err) {
      console.error('Failed to load discovery communities:', err);
      setDiscoverCommunities([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, selectedLocation, currentPage, isAuthenticated]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCommunities();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchCommunities]);

  const handleCategorySelect = (catId: string) => {
    if (selectedCategory === catId) return;
    setSelectedCategory(catId);
  };

  const closeConfirmModal = () => {
    if (confirmModal.isClosing) return;
    setConfirmModal((prev) => ({ ...prev, isClosing: true }));
    setTimeout(() => {
      setConfirmModal((prev) => ({ ...prev, isOpen: false, isClosing: false }));
    }, 280);
  };

  const promptJoin = (slug: string, name: string) => {
    runGuestAware(() => {
      setConfirmModal({
        isOpen: true,
        isClosing: false,
        type: 'join',
        slug,
        name,
      });
    });
  };

  const handleConfirmAction = async () => {
    const { type, slug, name } = confirmModal;
    closeConfirmModal();
    if (!slug) return;

    try {
      setActionLoadingSlug(slug);
      if (type === 'join') {
        const res = await communityService.joinCommunity(slug);
        if (res.success) {
          showToast.success(`Berhasil bergabung dengan ${name}!`);
          setJoinedSlugs((prev) => new Set([...prev, slug]));
        } else {
          showToast.error(res.message || 'Gagal bergabung dengan komunitas');
        }
      } else {
        const res = await communityService.leaveCommunity(slug);
        if (res.success) {
          showToast.success(`Telah keluar dari komunitas ${name}`);
          setJoinedSlugs((prev) => {
            const next = new Set(prev);
            next.delete(slug);
            return next;
          });
        } else {
          showToast.error(res.message || 'Gagal meninggalkan komunitas');
        }
      }
      await fetchCommunities();
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Terjadi kesalahan');
    } finally {
      setActionLoadingSlug(null);
    }
  };

  const closeModeModal = () => {
    if (isModeModalClosing) return;
    setIsModeModalClosing(true);
    setTimeout(() => {
      setIsModeModalOpen(false);
      setIsModeModalClosing(false);
    }, 280);
  };

  const handleSelectMode = (targetPath: string) => {
    closeModeModal();
    setTimeout(() => {
      navigate(targetPath);
    }, 200);
  };

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedLocation]);

  // Client-side filtering by category & location
  const filteredCommunities = discoverCommunities.filter((item) => {
    if (selectedCategory !== 'Semua') {
      if (item.sportCategoryName && !item.sportCategoryName.toLowerCase().includes(selectedCategory.toLowerCase())) {
        return false;
      }
    }
    if (selectedLocation) {
      if (item.location && !item.location.toLowerCase().includes(selectedLocation.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  const totalItems = totalMetaItems || filteredCommunities.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 1;
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);
  const paginatedCommunities = filteredCommunities;

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-24 md:pb-12">
      {/* Desktop View */}
      <div className="hidden md:block max-w-6xl mx-auto px-6 pt-6 pb-8 space-y-6 animate-fade-in">
        {/* Top Header & Mode Switcher */}
        <div className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-orange-50 border border-orange-100 text-[#FC480D] flex items-center justify-center shrink-0">
              <Icon icon="ph:users-three-bold" className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                MODE PENCARIAN
              </p>
              <h1 className="text-base font-bold text-[#0E1726] leading-tight">
                Cari komunitas
              </h1>
            </div>
          </div>

          {/* Desktop Mode Navigation Pills */}
          <div className="flex items-center gap-1.5 bg-gray-100/80 p-1 rounded-2xl border border-gray-200/60">
            <button
              type="button"
              onClick={() => navigate('/discovery/event')}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:text-[#0E1726] hover:bg-white/60 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:calendar-blank-bold" className="w-4 h-4" />
              <span>Cari event</span>
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-xl text-xs font-bold bg-white text-[#FC480D] shadow-2xs flex items-center gap-1.5 cursor-default"
            >
              <Icon icon="ph:users-three-bold" className="w-4 h-4" />
              <span>Cari komunitas</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/discovery/arena')}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:text-[#0E1726] hover:bg-white/60 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:buildings-bold" className="w-4 h-4" />
              <span>Booking lapangan</span>
            </button>
          </div>
        </div>

        {/* Desktop Filter Toolbar */}
        <div className="bg-white rounded-2xl border border-[#E0E6ED] p-3.5 shadow-2xs flex flex-wrap items-center justify-between gap-3">
          {/* Search Input & Location Dropdown */}
          <div className="flex items-center gap-2.5 flex-1 min-w-[320px] max-w-xl">
            <div className="relative flex-1">
              <Icon
                icon="ph:magnifying-glass-bold"
                className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Cari komunitas atau kota..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 pl-9 pr-8 rounded-xl border border-[#E0E6ED] bg-gray-50/50 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:bg-white shadow-2xs transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="w-48 shrink-0">
              <LocationSearchDropdown
                value={selectedLocation}
                onChange={setSelectedLocation}
              />
            </div>
          </div>

          {/* Sport Category Filter Pills */}
          <div className="flex items-center gap-2 flex-wrap">
            {CATEGORY_PILLS.map((pill) => (
              <button
                key={pill.id}
                type="button"
                onClick={() => handleCategorySelect(pill.id)}
                className={`h-11 px-4 rounded-xl text-xs font-bold shrink-0 transition-all duration-200 cursor-pointer shadow-2xs ${
                  selectedCategory === pill.id
                    ? 'bg-[#FC480D] text-white shadow-orange-200/50'
                    : 'bg-white text-gray-700 border border-[#E0E6ED] hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                {pill.label}
              </button>
            ))}
          </div>
        </div>

        {/* Section Header */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2.5">
            <h2 className="text-base font-bold text-[#0E1726]">
              Rekomendasi buat kamu
            </h2>
            {!loading && (
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-orange-50 text-[#FC480D] border border-orange-100">
                {totalItems} komunitas
              </span>
            )}
          </div>
        </div>

        {/* Community List Grid for Desktop */}
        {loading ? (
          <CommunityListSkeleton />
        ) : filteredCommunities.length === 0 ? (
          <Card className="p-12 text-center space-y-2">
            <Icon icon="ph:users-three-bold" className="w-12 h-12 text-gray-300 mx-auto" />
            <h3 className="text-sm font-bold text-[#0E1726]">Komunitas Tidak Ditemukan</h3>
            <p className="text-xs text-gray-400 max-w-xs mx-auto">
              Tidak ada komunitas yang cocok dengan pencarian atau filter lokasi Anda saat ini.
            </p>
          </Card>
        ) : (
          <div key={`desktop-${selectedCategory}-${currentPage}`} className="space-y-6 animate-in fade-in duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {paginatedCommunities.map((item) => {
                const isProcessing = actionLoadingSlug === item.slug;
                const isJoined = joinedSlugs.has(item.slug);

                const communityEvent = upcomingEventsMap[item.slug] || upcomingEventsMap[item.name.toLowerCase()];
                const eventInfo = communityEvent
                  ? formatEventInfo(communityEvent.event_date, communityEvent.start_time)
                  : null;

                const activity = calculateActivityStatus(communityEvent?.event_date);
                const activePct = activity.pct;
                const activeStatusText = activity.label;

                return (
                  <Card
                    key={item.slug}
                    className={`p-5 space-y-3.5 hover:shadow-md hover:border-orange-200 hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between ${
                      isProcessing ? 'bg-[#F8FAFC] opacity-70 animate-pulse' : ''
                    }`}
                  >
                    <div className="space-y-3.5">
                      {/* Community Header Row */}
                      <div className="flex items-start gap-3.5">
                        {item.logo ? (
                          <img
                            src={item.logo}
                            alt={item.name}
                            className="w-12 h-12 rounded-2xl object-cover shrink-0 border border-gray-100 shadow-2xs"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 text-white font-bold text-base flex items-center justify-center shrink-0 border border-orange-100 shadow-2xs">
                            {getInitials(item.name)}
                          </div>
                        )}

                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-1.5">
                            <h3
                              onClick={() => navigate(`/community/${item.slug}`)}
                              className="text-sm font-bold text-[#0E1726] hover:text-[#FC480D] cursor-pointer transition-colors truncate"
                            >
                              {item.name}
                            </h3>
                            {item.is_verified && (
                              <Icon icon="ph:seal-check-fill" className="w-4 h-4 text-blue-500 shrink-0" />
                            )}
                          </div>

                          <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium truncate">
                            <Icon icon="ph:users-bold" className="w-3.5 h-3.5 shrink-0" />
                            <span>{item.totalMembers.toLocaleString('id-ID')} anggota</span>
                            {item.location && (
                              <>
                                <span>·</span>
                                <span className="truncate">{item.location}</span>
                              </>
                            )}
                          </div>

                          {/* Sport Category Badges */}
                          <div className="flex items-center gap-1.5 pt-0.5 flex-wrap">
                            <Badge variant="orange" className="text-[10px] px-2.5 py-0.5 font-bold">
                              {item.sportCategoryName || 'Olahraga'}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Activity Progress Bar */}
                      <div className="flex items-center justify-between gap-3 text-xs">
                        <span className="text-gray-400 font-medium text-[11px]">Aktivitas</span>
                        <div className="h-1.5 flex-1 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${
                              activePct >= 80 ? 'bg-emerald-500' : activePct >= 60 ? 'bg-[#FC480D]' : 'bg-gray-300'
                            }`}
                            style={{ width: `${activePct}%` }}
                          />
                        </div>
                        <span
                          className={`font-bold text-[11px] shrink-0 ${
                            activePct >= 80 ? 'text-emerald-600' : activePct >= 60 ? 'text-orange-600' : 'text-gray-400'
                          }`}
                        >
                          {activeStatusText}
                        </span>
                      </div>
                    </div>

                    {/* Join Action Footer with Event Info */}
                    <div className="border-t border-gray-100 pt-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {eventInfo && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 min-w-0 truncate">
                            <Icon
                              icon="ph:calendar-blank-bold"
                              className={`w-4 h-4 shrink-0 ${eventInfo.isFuture ? 'text-[#FC480D]' : 'text-gray-400'}`}
                            />
                            <span className="truncate sm:text-xs font-semibold text-[#0E1726]">
                              {eventInfo.label}{' '}
                              <span className={eventInfo.isFuture ? 'text-[#FC480D] font-bold' : 'text-gray-500 font-semibold'}>
                                {eventInfo.text}
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                      {isJoined ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/community/${item.slug}`)}
                          className="h-9 px-4 rounded-full bg-orange-50 hover:bg-orange-100 text-[#FC480D] font-bold text-xs shrink-0 border border-orange-200 gap-1.5 cursor-pointer"
                        >
                          <Icon icon="ph:info-bold" className="w-4 h-4 text-[#FC480D] me-2" />
                          <span>Detail</span>
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          disabled={isProcessing}
                          onClick={() => promptJoin(item.slug, item.name)}
                          className="h-9 px-4 rounded-full font-bold text-xs shrink-0 gap-1.5"
                        >
                          {isProcessing ? (
                            <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-white" />
                          ) : (
                            <>
                              <Icon icon="ph:user-plus-bold" className="w-4 h-4 me-2" />
                              <span>Gabung</span>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Desktop Pagination Controls */}
            {totalPages > 1 && (
              <div className="pt-2 flex items-center justify-between gap-3">
                <span className="text-xs text-gray-400 font-medium">
                  {startItem}–{endItem} dari {totalItems} komunitas
                </span>

                <div className="flex items-center gap-1.5 flex-wrap justify-center">
                  <button
                    disabled={currentPage <= 1 || loading}
                    onClick={() => {
                      setCurrentPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors cursor-pointer shrink-0"
                  >
                    <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
                  </button>

                  {getPaginationItems(currentPage, totalPages).map((item, idx) => {
                    if (item === '...') {
                      return (
                        <span
                          key={`ellipsis-desktop-${idx}`}
                          className="w-6 h-8 flex items-center justify-center text-xs text-gray-400 font-bold select-none shrink-0"
                        >
                          ...
                        </span>
                      );
                    }

                    const pNum = item as number;
                    return (
                      <button
                        key={`p-desktop-${pNum}`}
                        onClick={() => {
                          setCurrentPage(pNum);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center transition-all cursor-pointer shrink-0 ${
                          currentPage === pNum
                            ? 'bg-[#FC480D] text-white shadow-2xs'
                            : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pNum}
                      </button>
                    );
                  })}

                  <button
                    disabled={currentPage >= totalPages || loading}
                    onClick={() => {
                      setCurrentPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors cursor-pointer shrink-0"
                  >
                    <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        <div className="max-w-2xl mx-auto px-4 pt-4 md:pt-6 space-y-4 animate-fade-in">
        {/* Top Card: MODE PENCARIAN */}
        <div className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-orange-50 border border-orange-100 text-[#FC480D] flex items-center justify-center shrink-0">
              <Icon icon="ph:users-three-bold" className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                MODE PENCARIAN
              </p>
              <h2 className="text-sm font-bold text-[#0E1726] leading-tight">
                Cari komunitas
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsModeModalOpen(true)}
            className="flex items-center gap-1 text-xs font-bold text-[#FC480D] hover:text-[#D83B08] transition-colors py-1.5 px-3 rounded-full hover:bg-orange-50 cursor-pointer"
          >
            <span>Ganti</span>
            <Icon icon="ph:arrows-clockwise-bold" className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Controls Row: Search Input & Location Dropdown */}
        <div className="grid grid-cols-3 gap-2.5">
          <div className="col-span-2 relative flex items-center">
            <Icon
              icon="ph:magnifying-glass-bold"
              className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Cari komunitas atau kota"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-9 pr-3 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs transition-colors"
            />
          </div>
          <LocationSearchDropdown
            value={selectedLocation}
            onChange={setSelectedLocation}
          />
        </div>

        {/* Sport Category Filter Pills Row */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {CATEGORY_PILLS.map((pill) => (
            <button
              key={pill.id}
              type="button"
              onClick={() => handleCategorySelect(pill.id)}
              className={`h-9 px-4 rounded-full text-xs font-bold shrink-0 transition-all duration-200 ease-out active:scale-95 cursor-pointer shadow-2xs ${
                selectedCategory === pill.id
                  ? 'bg-[#FC480D] text-white shadow-orange-200/50 scale-102'
                  : 'bg-white text-gray-700 border border-[#E0E6ED] hover:bg-gray-50 hover:border-gray-300'
              }`}
            >
              {pill.label}
            </button>
          ))}
        </div>

        {/* Section Header Title */}
        <div className="flex items-center justify-between pt-1">
          <h2 className="text-base font-bold text-[#0E1726]">
            Rekomendasi buat kamu
          </h2>
        </div>

        {/* Community List */}
        {loading ? (
          <CommunityListSkeleton />
        ) : filteredCommunities.length === 0 ? (
          <Card className="p-8 text-center space-y-2">
            <Icon icon="ph:users-three-bold" className="w-10 h-10 text-gray-300 mx-auto" />
            <h3 className="text-sm font-bold text-[#0E1726]">Komunitas Tidak Ditemukan</h3>
            <p className="text-xs text-gray-400 max-w-xs mx-auto">
              Tidak ada komunitas yang cocok dengan pencarian atau filter lokasi Anda saat ini.
            </p>
          </Card>
        ) : (
          <div key={`${selectedCategory}-${currentPage}`} className="space-y-4 animate-in fade-in duration-300">
            {paginatedCommunities.map((item) => {
              const isProcessing = actionLoadingSlug === item.slug;
              const isJoined = joinedSlugs.has(item.slug);

              // Get upcoming or past event info from DB API map
              const communityEvent = upcomingEventsMap[item.slug] || upcomingEventsMap[item.name.toLowerCase()];
              const eventInfo = communityEvent
                ? formatEventInfo(communityEvent.event_date, communityEvent.start_time)
                : null;

              // Calculate real activity percentage and status label based on latest event date
              const activity = calculateActivityStatus(communityEvent?.event_date);
              const activePct = activity.pct;
              const activeStatusText = activity.label;

              return (
                <Card
                  key={item.slug}
                  className={`p-4 sm:p-5 space-y-3.5 hover:shadow-xs transition-all duration-200 ${
                    isProcessing ? 'bg-[#F8FAFC] opacity-70 animate-pulse' : ''
                  }`}
                >
                  {/* Community Header Row */}
                  <div className="flex items-start gap-3.5">
                    {item.logo ? (
                      <img
                        src={item.logo}
                        alt={item.name}
                        className="w-12 h-12 rounded-2xl object-cover shrink-0 border border-gray-100 shadow-2xs"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 text-white font-bold text-base flex items-center justify-center shrink-0 border border-orange-100 shadow-2xs">
                        {getInitials(item.name)}
                      </div>
                    )}

                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <h3
                          onClick={() => navigate(`/community/${item.slug}`)}
                          className="text-sm font-bold text-[#0E1726] hover:text-[#FC480D] cursor-pointer transition-colors truncate"
                        >
                          {item.name}
                        </h3>
                        {item.is_verified && (
                          <Icon icon="ph:seal-check-fill" className="w-4 h-4 text-blue-500 shrink-0" />
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium truncate">
                        <Icon icon="ph:users-bold" className="w-3.5 h-3.5 shrink-0" />
                        <span>{item.totalMembers.toLocaleString('id-ID')} anggota</span>
                        {item.location && (
                          <>
                            <span>·</span>
                            <span className="truncate">{item.location}</span>
                          </>
                        )}
                      </div>

                      {/* Sport Category Badges */}
                      <div className="flex items-center gap-1.5 pt-0.5 flex-wrap">
                        <Badge variant="orange" className="text-[10px] px-2.5 py-0.5 font-bold">
                          {item.sportCategoryName || 'Olahraga'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Activity Progress Bar */}
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="text-gray-400 font-medium text-[11px]">Aktivitas</span>
                    <div className="h-1.5 flex-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          activePct >= 80 ? 'bg-emerald-500' : activePct >= 60 ? 'bg-[#FC480D]' : 'bg-gray-300'
                        }`}
                        style={{ width: `${activePct}%` }}
                      />
                    </div>
                    <span
                      className={`font-bold text-[11px] shrink-0 ${
                        activePct >= 80 ? 'text-emerald-600' : activePct >= 60 ? 'text-orange-600' : 'text-gray-400'
                      }`}
                    >
                      {activeStatusText}
                    </span>
                  </div>

                  {/* Join Action Footer with Event Info */}
                  <div className="border-t border-gray-100 pt-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {eventInfo && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 min-w-0 truncate">
                          <Icon
                            icon="ph:calendar-blank-bold"
                            className={`w-4 h-4 shrink-0 ${eventInfo.isFuture ? 'text-[#FC480D]' : 'text-gray-400'}`}
                          />
                          <span className="truncate sm:text-xs font-semibold text-[#0E1726]">
                            {eventInfo.label}{' '}
                            <span className={eventInfo.isFuture ? 'text-[#FC480D] font-bold' : 'text-gray-500 font-semibold'}>
                              {eventInfo.text}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                    {isJoined ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/community/${item.slug}`)}
                        className="h-9 px-4 rounded-full bg-orange-50 hover:bg-orange-100 text-[#FC480D] font-bold text-xs shrink-0 border border-orange-200 gap-1.5 cursor-pointer"
                      >
                        <Icon icon="ph:info-bold" className="w-4 h-4 text-[#FC480D] me-2" />
                        <span>Detail</span>
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        disabled={isProcessing}
                        onClick={() => promptJoin(item.slug, item.name)}
                        className="h-9 px-4 rounded-full font-bold text-xs shrink-0 gap-1.5"
                      >
                        {isProcessing ? (
                          <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-white" />
                        ) : (
                          <>
                            <Icon icon="ph:user-plus-bold" className="w-4 h-4 me-2" />
                            <span>Gabung</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3">
                <span className="text-xs text-gray-400 font-medium">
                  {startItem}–{endItem} dari {totalItems} komunitas
                </span>

                <div className="flex items-center gap-1.5 flex-wrap justify-center">
                  <button
                    disabled={currentPage <= 1 || loading}
                    onClick={() => {
                      setCurrentPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors cursor-pointer shrink-0"
                  >
                    <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
                  </button>

                  {getPaginationItems(currentPage, totalPages).map((item, idx) => {
                    if (item === '...') {
                      return (
                        <span
                          key={`ellipsis-${idx}`}
                          className="w-6 h-8 flex items-center justify-center text-xs text-gray-400 font-bold select-none shrink-0"
                        >
                          ...
                        </span>
                      );
                    }

                    const pNum = item as number;
                    return (
                      <button
                        key={pNum}
                        onClick={() => {
                          setCurrentPage(pNum);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center transition-all cursor-pointer shrink-0 ${
                          currentPage === pNum
                            ? 'bg-[#FC480D] text-white shadow-2xs'
                            : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pNum}
                      </button>
                    );
                  })}

                  <button
                    disabled={currentPage >= totalPages || loading}
                    onClick={() => {
                      setCurrentPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors cursor-pointer shrink-0"
                  >
                    <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Bottom Popup Confirmation Modal (Same as /my-community) */}
      {confirmModal.isOpen && (
        <div
          className={`fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end sm:items-center justify-center transition-opacity duration-300 ease-in-out ${
            confirmModal.isClosing ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-in fade-in duration-200'
          }`}
          onClick={closeConfirmModal}
        >
          <div
            className={`w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl transition-transform duration-300 ease-in-out ${
              confirmModal.isClosing
                ? 'translate-y-full'
                : 'translate-y-0 animate-in slide-in-from-bottom sm:zoom-in-95 duration-300'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle bar */}
            <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 mb-1" />

            {/* Icon & Title */}
            <div className="flex flex-col items-center text-center space-y-2 pt-1">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center border shadow-2xs ${
                  confirmModal.type === 'join'
                    ? 'bg-orange-50 text-[#FC480D] border-orange-100'
                    : 'bg-rose-50 text-rose-600 border-rose-100'
                }`}
              >
                <Icon
                  icon={confirmModal.type === 'join' ? 'ph:users-three-bold' : 'ph:user-minus-bold'}
                  className="w-7 h-7"
                />
              </div>
              <h3 className="text-lg font-bold text-[#0E1726]">
                {confirmModal.type === 'join' ? 'Bergabung ke Komunitas?' : 'Tinggalkan Komunitas?'}
              </h3>
              <p className="text-xs text-gray-500 font-medium max-w-xs leading-relaxed">
                {confirmModal.type === 'join' ? (
                  <>Apakah Anda yakin ingin bergabung dengan komunitas <strong className="text-[#0E1726]">{confirmModal.name}</strong>?</>
                ) : (
                  <>Apakah Anda yakin ingin keluar dari komunitas <strong className="text-[#0E1726]">{confirmModal.name}</strong>?</>
                )}
              </p>
            </div>

            {/* Action Buttons Row */}
            <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={closeConfirmModal}
                className="flex-1 h-11 rounded-full border border-[#E0E6ED] bg-white text-[#0E1726] font-bold text-xs hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                className={`flex-1 h-11 rounded-full font-bold text-xs text-white shadow-2xs transition-all active:scale-98 cursor-pointer ${
                  confirmModal.type === 'join'
                    ? 'bg-[#FC480D] hover:bg-[#D83B08]'
                    : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                {confirmModal.type === 'join' ? 'Ya, Bergabung' : 'Ya, Tinggalkan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Mode Bottom Sheet Popup Modal */}
      {isModeModalOpen && (
        <div
          className={`fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end justify-center transition-opacity duration-300 ease-in-out ${
            isModeModalClosing ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-in fade-in duration-200'
          }`}
          onClick={closeModeModal}
        >
          <div
            className={`w-full max-w-md bg-white rounded-t-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl transition-transform duration-300 ease-in-out ${
              isModeModalClosing
                ? 'translate-y-full'
                : 'translate-y-0 animate-in slide-in-from-bottom duration-300'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle bar */}
            <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 mb-1" />

            <div className="space-y-1">
              <h2 className="text-lg sm:text-xl font-bold text-[#0E1726]">
                Pilih Mode Pencarian
              </h2>
              <p className="text-xs text-gray-400 font-medium">
                Pilih satu untuk melihat jenis penjelajahan lain
              </p>
            </div>

            <div className="space-y-3 pt-1">
              {[
                {
                  id: 'event',
                  title: 'Cari event',
                  subtitle: 'Mabar, sparing & turnamen di sekitarmu',
                  icon: 'ph:calendar-blank-bold',
                  bgColor: 'bg-orange-50 border-orange-100',
                  iconColor: 'text-[#FC480D]',
                  path: '/discovery/event',
                },
                {
                  id: 'community',
                  title: 'Cari komunitas',
                  subtitle: 'Gabung grup yang rutin main tiap pekan',
                  icon: 'ph:users-three-bold',
                  bgColor: 'bg-blue-50 border-blue-100',
                  iconColor: 'text-blue-600',
                  path: '/discovery/community',
                },
                {
                  id: 'booking',
                  title: 'Booking lapangan',
                  subtitle: 'Sewa lapangan sesuai jam & lokasi',
                  icon: 'ph:buildings-bold',
                  bgColor: 'bg-emerald-50 border-emerald-100',
                  iconColor: 'text-emerald-600',
                  path: '/discovery/arena',
                },
              ].map((opt) => (
                <div
                  key={opt.id}
                  onClick={() => handleSelectMode(opt.path)}
                  className="w-full p-4 rounded-2xl bg-white border border-[#E0E6ED] shadow-2xs flex items-center justify-between transition-all cursor-pointer group active:scale-[0.99]"
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${opt.bgColor} ${opt.iconColor}`}
                    >
                      <Icon icon={opt.icon} className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-[#0E1726] group-hover:text-[#FC480D] transition-colors truncate">
                        {opt.title}
                      </h3>
                      <p className="text-xs text-gray-400 font-medium truncate mt-0.5">
                        {opt.subtitle}
                      </p>
                    </div>
                  </div>
                  <Icon
                    icon="ph:caret-right-bold"
                    className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0"
                  />
                </div>
              ))}
            </div>

            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={closeModeModal}
                className="text-xs font-semibold text-gray-500 hover:text-[#0E1726] transition-colors py-2 px-4 rounded-full cursor-pointer"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDiscoveryCommunityPage;
